/**
 * QuestionSetCsvImport Component
 * Requirement 49.8: Multi-language CSV import for question sets
 */
import { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  LinearProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import {
  parseQuestionSetCSV,
  downloadCsvTemplate,
  countTranslations,
  LANGUAGE_LABELS,
  type ParsedQuestion,
  type QuestionSetCsvParseResult,
  type SupportedLanguage,
} from '../utils/questionSetCsvParser';

interface QuestionSetCsvImportProps {
  onImport: (result: QuestionSetCsvParseResult) => void;
  onCancel: () => void;
  isImporting?: boolean;
}

/**
 * Translation coverage indicator
 */
function TranslationCoverage({
  label,
  translated,
  total,
}: {
  label: string;
  translated: number;
  total: number;
}) {
  const percentage = total > 0 ? Math.round((translated / total) * 100) : 0;
  const color = percentage === 100 ? 'success' : percentage > 0 ? 'warning' : 'default';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Typography variant="caption" sx={{ minWidth: 80 }}>
        {label}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={percentage}
        color={color as 'success' | 'warning' | 'inherit'}
        sx={{ flex: 1, height: 8, borderRadius: 4 }}
      />
      <Typography variant="caption" sx={{ minWidth: 45 }}>
        {translated}/{total}
      </Typography>
    </Box>
  );
}

/**
 * Question preview row with multi-language support
 */
function QuestionPreviewRow({
  question,
  index,
  showTranslations,
}: {
  question: ParsedQuestion;
  index: number;
  showTranslations: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const hasTranslations =
    question.textEn ||
    question.textId ||
    question.textMn ||
    question.textNe ||
    question.textVi ||
    question.choiceA.textEn ||
    question.choiceB.textEn;

  return (
    <>
      <TableRow hover>
        <TableCell>{index + 1}</TableCell>
        <TableCell>
          <Box>
            <Typography variant="body2">{question.text}</Typography>
            {showTranslations && hasTranslations && (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ ml: 1 }}
              >
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Tooltip title={`コード: ${question.choiceA.thinkingTypeCode}`}>
            <Typography variant="body2">{question.choiceA.text}</Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title={`コード: ${question.choiceB.thinkingTypeCode}`}>
            <Typography variant="body2">{question.choiceB.text}</Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          {hasTranslations ? (
            <Chip
              size="small"
              icon={<TranslateIcon />}
              label="翻訳あり"
              color="info"
              variant="outlined"
            />
          ) : (
            <Chip size="small" label="日本語のみ" variant="outlined" />
          )}
        </TableCell>
      </TableRow>
      {showTranslations && hasTranslations && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0 }}>
            <Collapse in={expanded}>
              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  翻訳データ
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>言語</TableCell>
                      <TableCell>質問文</TableCell>
                      <TableCell>選択肢A</TableCell>
                      <TableCell>選択肢B</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(['en', 'id', 'mn', 'ne', 'vi'] as SupportedLanguage[]).map((lang) => {
                      const qText = question[`text${lang.charAt(0).toUpperCase()}${lang.slice(1)}` as keyof ParsedQuestion] as string | undefined;
                      const aText = question.choiceA[`text${lang.charAt(0).toUpperCase()}${lang.slice(1)}` as keyof typeof question.choiceA] as string | undefined;
                      const bText = question.choiceB[`text${lang.charAt(0).toUpperCase()}${lang.slice(1)}` as keyof typeof question.choiceB] as string | undefined;

                      if (!qText && !aText && !bText) return null;

                      return (
                        <TableRow key={lang}>
                          <TableCell>{LANGUAGE_LABELS[lang]}</TableCell>
                          <TableCell>{qText || '-'}</TableCell>
                          <TableCell>{aText || '-'}</TableCell>
                          <TableCell>{bText || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function QuestionSetCsvImport({
  onImport,
  onCancel,
  isImporting = false,
}: QuestionSetCsvImportProps) {
  const [parseResult, setParseResult] = useState<QuestionSetCsvParseResult | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewTab, setPreviewTab] = useState(0);

  // Handle file selection
  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      setParseResult({
        questionSetTitle: '',
        questions: [],
        isMultiLanguage: false,
        errors: [{ row: 0, message: 'CSVファイルを選択してください' }],
        isValid: false,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvData = e.target?.result as string;
      const result = parseQuestionSetCSV(csvData);
      setParseResult(result);
    };
    reader.readAsText(file, 'UTF-8');
  }, []);

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFile(e.dataTransfer.files[0]);
      }
    },
    [handleFile]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        handleFile(e.target.files[0]);
      }
    },
    [handleFile]
  );

  // Translation statistics
  const translationStats = useMemo(() => {
    if (!parseResult || !parseResult.isMultiLanguage) return null;
    return countTranslations(parseResult.questions);
  }, [parseResult]);

  const handleImport = useCallback(() => {
    if (parseResult && parseResult.isValid) {
      onImport(parseResult);
    }
  }, [parseResult, onImport]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1">
          CSVインポート
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadCsvTemplate(false)}
            size="small"
          >
            標準テンプレート
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadCsvTemplate(true)}
            size="small"
            color="info"
          >
            多言語テンプレート
          </Button>
        </Box>
      </Box>

      {/* File Upload Area */}
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: dragActive ? 'primary.main' : 'divider',
          bgcolor: dragActive ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s',
          mb: 3,
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById('csv-file-input')?.click()}
      >
        <input
          id="csv-file-input"
          type="file"
          accept=".csv"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="body1" gutterBottom>
          CSVファイルをドラッグ＆ドロップまたはクリックして選択
        </Typography>
        <Typography variant="caption" color="text.secondary">
          標準フォーマット（6列）または多言語フォーマット（21列）に対応
        </Typography>
      </Paper>

      {/* Parse Results */}
      {parseResult && (
        <Box>
          {/* Status */}
          {parseResult.isValid ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckIcon />
                <Typography>
                  {parseResult.questions.length} 件の質問を読み込みました
                  {parseResult.isMultiLanguage && ' (多言語フォーマット)'}
                </Typography>
                {parseResult.questionSetTitle && (
                  <Chip
                    size="small"
                    label={`タイトル: ${parseResult.questionSetTitle}`}
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            </Alert>
          ) : (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography>CSVの解析に失敗しました</Typography>
            </Alert>
          )}

          {/* Errors */}
          {parseResult.errors.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'error.50' }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                <ErrorIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                エラー ({parseResult.errors.length} 件)
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {parseResult.errors.slice(0, 10).map((error, i) => (
                  <li key={i}>
                    <Typography variant="body2" color="error">
                      {error.row > 0 && `行 ${error.row}: `}
                      {error.field && `[${error.field}] `}
                      {error.message}
                    </Typography>
                  </li>
                ))}
                {parseResult.errors.length > 10 && (
                  <li>
                    <Typography variant="body2" color="error">
                      ...他 {parseResult.errors.length - 10} 件のエラー
                    </Typography>
                  </li>
                )}
              </Box>
            </Paper>
          )}

          {/* Translation Statistics (for multi-language format) */}
          {parseResult.isMultiLanguage && translationStats && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                <TranslateIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                翻訳カバレッジ
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    質問文
                  </Typography>
                  {(['en', 'id', 'mn', 'ne', 'vi'] as SupportedLanguage[]).map((lang) => (
                    <TranslationCoverage
                      key={lang}
                      label={LANGUAGE_LABELS[lang]}
                      translated={translationStats.translatedQuestions[lang]}
                      total={translationStats.totalQuestions}
                    />
                  ))}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    選択肢
                  </Typography>
                  {(['en', 'id', 'mn', 'ne', 'vi'] as SupportedLanguage[]).map((lang) => (
                    <TranslationCoverage
                      key={lang}
                      label={LANGUAGE_LABELS[lang]}
                      translated={translationStats.translatedChoices[lang]}
                      total={translationStats.totalChoices}
                    />
                  ))}
                </Box>
              </Box>
            </Paper>
          )}

          {/* Preview Table */}
          {parseResult.questions.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                <Tabs value={previewTab} onChange={(_, v) => setPreviewTab(v)}>
                  <Tab label={`プレビュー (${parseResult.questions.length} 件)`} />
                </Tabs>
              </Box>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={60}>#</TableCell>
                      <TableCell>質問文</TableCell>
                      <TableCell>選択肢A</TableCell>
                      <TableCell>選択肢B</TableCell>
                      <TableCell width={100}>翻訳</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parseResult.questions.slice(0, 50).map((question, index) => (
                      <QuestionPreviewRow
                        key={index}
                        question={question}
                        index={index}
                        showTranslations={parseResult.isMultiLanguage}
                      />
                    ))}
                    {parseResult.questions.length > 50 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary">
                            ...他 {parseResult.questions.length - 50} 件
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}

      {/* Actions */}
      <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
        <Button onClick={onCancel} disabled={isImporting}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!parseResult?.isValid || isImporting}
          startIcon={isImporting ? undefined : <UploadIcon />}
        >
          {isImporting ? 'インポート中...' : 'インポート'}
        </Button>
      </Box>
    </Box>
  );
}
